import React, { useState, useEffect } from 'react';
import api from '../../api';
import './RSVPModal.css';

export default function RSVPModal({ isOpen, onClose }) {
    const [statusData, setStatusData] = useState({
        totalSeats: 150,
        remainingSeats: 150,
        isSoldOut: false,
        loading: false
    });
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [seats, setSeats] = useState(1);
    const [isWaitlistMode, setIsWaitlistMode] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [successData, setSuccessData] = useState(null);

    const fetchStatus = async () => {
        try {
            const res = await api.get('rsvp/status/');
            const data = res.data;
            setStatusData({
                totalSeats: data.total_seats,
                remainingSeats: data.remaining_seats,
                isSoldOut: data.is_sold_out,
                loading: false
            });
            if (data.is_sold_out) {
                setIsWaitlistMode(true);
            }
        } catch (err) {
            console.error('Error fetching RSVP status:', err);
            setStatusData(prev => ({ ...prev, loading: false }));
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchStatus();
            setError(null);
            setSuccessData(null);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!name.trim() || !email.trim() || !phone.trim()) {
            setError('Please enter your full name, email address, and phone number.');
            return;
        }

        setSubmitting(true);

        try {
            const response = await api.post('rsvp/register/', {
                name: name.trim(),
                email: email.trim().toLowerCase(),
                phone: phone.trim(),
                seats: parseInt(seats, 10),
                is_waitlist: isWaitlistMode
            });

            setSuccessData(response.data);
            setSubmitting(false);
            fetchStatus(); // Refresh counter
        } catch (err) {
            setSubmitting(false);
            const errRes = err.response?.data;
            if (errRes?.is_sold_out) {
                setIsWaitlistMode(true);
                setError(errRes.error || 'The event is sold out! Click below to join the waitlist.');
            } else {
                setError(errRes?.error || 'Registration failed. Please try again.');
            }
        }
    };

    return (
        <div className="rsvp-modal-overlay" onClick={onClose}>
            <div className="rsvp-modal-card" onClick={e => e.stopPropagation()}>
                <button className="rsvp-modal-close" onClick={onClose} aria-label="Close modal">
                    &times;
                </button>

                {successData ? (
                    <div className="rsvp-success-view">
                        <div className="success-icon-badge">✓</div>
                        <span className="gold-subtitle">
                            {successData.rsvp.is_waitlist ? 'WAITLIST REGISTERED' : 'WORLD PREMIERE CONFIRMED'}
                        </span>
                        <h2 className="success-title">
                            {successData.rsvp.is_waitlist ? "You're on the Waiting List!" : "You're Attending!"}
                        </h2>
                        <p className="success-msg">
                            {successData.message} A confirmation email has been sent to <strong>{successData.rsvp.email}</strong>.
                        </p>

                        <div className="ticket-summary-box">
                            <div className="ticket-header">
                                <span className="ticket-label">TICKET CODE</span>
                                <span className="ticket-code-val">{successData.rsvp.ticket_code}</span>
                            </div>
                            <div className="ticket-details">
                                <p><strong>Name:</strong> {successData.rsvp.name}</p>
                                <p><strong>Seats:</strong> {successData.rsvp.seats} Seat{successData.rsvp.seats > 1 ? 's' : ''}</p>
                                <p><strong>Date:</strong> 12:30 PM Sat, 29 August 2026</p>
                                <p><strong>Venue:</strong> Bellmore Playhouse, NY 11710</p>
                            </div>
                        </div>

                        <button className="btn-rsvp-done" onClick={onClose}>
                            Close & Return
                        </button>

                        <p className="rsvp-contact-note">
                            Have a question? Please contact <a href="mailto:support@gawahmedia.com">support@gawahmedia.com</a> or <a href="tel:+17183148171">+1 718-314-8171</a>
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="rsvp-modal-header">
                            <span className="rsvp-badge-subtitle">EXCLUSIVE INVITATION</span>
                            <h2 className="rsvp-modal-title">World Premiere RSVP</h2>
                            <p className="rsvp-event-tag">
                                <strong>GAWAH – The Witness</strong> | Sat, Aug 29 @ 12:30 PM<br />
                            </p>
                            <p className="rsvp-event-tag">
                                <strong>Bellmore Playhouse, 525 Bedford Ave, Bellmore, NY</strong>
                            </p>
                            {/* Seat counter section commented out until ticket bookings start
                            <div className="rsvp-counter-bar">
                                {statusData.loading ? (
                                    <span className="counter-loading">Checking seat availability...</span>
                                ) : statusData.isSoldOut || isWaitlistMode ? (
                                    <span className="counter-soldout">🔴 SOLD OUT — JOIN THE WAITLIST</span>
                                ) : (
                                    <span className="counter-available">
                                        🔥 Only <strong>{statusData.remainingSeats}</strong> of 150 Seats Remaining
                                    </span>
                                )}
                            </div>
                            */}
                        </div>

                        {error && <div className="rsvp-error-banner">{error}</div>}

                        <form onSubmit={handleSubmit} className="rsvp-form">
                            <div className="form-group">
                                <label>Full Name</label>
                                <input
                                    type="text"
                                    placeholder="e.g. John Doe"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Email Address</label>
                                <input
                                    type="email"
                                    placeholder="your.email@example.com"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Phone Number</label>
                                <input
                                    type="tel"
                                    placeholder="+1 (718) 000 - 0000"
                                    value={phone}
                                    onChange={e => setPhone(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Number of Seats (Max 2)</label>
                                <div className="seats-selector">
                                    <button
                                        type="button"
                                        className={`seat-btn ${seats === 1 ? 'active' : ''}`}
                                        onClick={() => setSeats(1)}
                                    >
                                        1 Seat
                                    </button>
                                    <button
                                        type="button"
                                        className={`seat-btn ${seats === 2 ? 'active' : ''}`}
                                        onClick={() => setSeats(2)}
                                    >
                                        2 Seats
                                    </button>
                                </div>
                            </div>

                            <div className="rsvp-form-actions">
                                {isWaitlistMode ? (
                                    <button type="submit" className="btn-rsvp-waitlist" disabled={submitting}>
                                        {submitting ? 'Submitting...' : 'Add Me to Waitlist'}
                                    </button>
                                ) : (
                                    <button type="submit" className="btn-rsvp-submit" disabled={submitting || statusData.remainingSeats === 0}>
                                        {submitting ? 'Securing Seats...' : 'Confirm RSVP'}
                                    </button>
                                )}
                            </div>

                            <p className="rsvp-contact-note">
                                Have a question? Please contact <a href="mailto:support@gawahmedia.com">support@gawahmedia.com</a> or <a href="tel:+17183148171">+1 718-314-8171</a>
                            </p>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
